import React, { useMemo } from "react";
import { Download, BoxArrowUpRight } from "react-bootstrap-icons";
import { BaseWidget, WidgetIconButton } from "../common";
import { formatNumber } from "../../constants";
import { formatCurrency } from "../../constants";
import "../../styles/CommissionWidgets.css";

const STATUS_BADGE_CLASS = {
  Approved: "cp-approval__badge--ready",
  Pending: "cp-approval__badge--pending",
  Declined: "cp-approval__badge--pending",
};

function getAgentName(agent) {
  if (!agent) return "Unassigned";
  if (typeof agent === "string") return agent;
  const name = `${agent.firstName ?? ""} ${agent.lastName ?? ""}`.trim();
  return name || "Unassigned";
}

export function CommissionApprovalWidget({
  data,
  isLoading,
  error,
  onNavigate,
  onDownload,
}) {
  const rows = data?.results ?? data?.contracts ?? [];
  const pendingCount = useMemo(
    () =>
      rows.filter((r) => String(r.commissionStatus ?? "") === "Pending").length,
    [rows],
  );

  const asOf = useMemo(() => {
    const d = new Date();
    // show "as of yesterday" outside business hours, otherwise "as of today"
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  return (
    <BaseWidget
      className="cp-cwidget"
      title="Commission Approval"
      subtitle={rows.length > 0 ? `As of ${asOf}` : null}
      isLoading={isLoading}
      error={error}
      headerRight={
        <>
          {onDownload && (
            <WidgetIconButton
              icon={<Download />}
              title="Download"
              onClick={onDownload}
            />
          )}
          {onNavigate && (
            <WidgetIconButton
              icon={<BoxArrowUpRight />}
              title="View all"
              onClick={() => onNavigate("/commissions-contracts")}
            />
          )}
        </>
      }
    >
      {rows.length > 0 && (
        <div className="cp-cwidget__head">
          <span className="cp-cwidget__total">{formatNumber(rows.length)}</span>
          <span className="cp-cwidget__total-label"> contracts</span>
          <div className="cp-cwidget__meta">
            {formatNumber(pendingCount)} pending approval
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="cp-cwidget__meta">No pending approvals.</p>
      ) : (
        <div className="cp-cwidget__body">
          <div className="cp-cwidget__thead cp-approval__grid">
            <span className="cp-cwidget__th">Contract</span>
            <span className="cp-cwidget__th">Agent</span>
            <span className="cp-cwidget__th cp-cwidget__th--right">
              Net Price
            </span>
            <span className="cp-cwidget__th cp-cwidget__th--right">Status</span>
          </div>

          {rows.map((row, i) => {
            const contractNumber = row.number ?? row.contractNumber;
            const status = row.commissionStatus ?? row.status ?? "Pending";
            const agentName = getAgentName(row.agent);

            return (
              <div
                key={contractNumber ?? i}
                className="cp-cwidget__row cp-approval__grid"
              >
                <div>
                  {onNavigate && contractNumber ? (
                    <button
                      type="button"
                      className="cp-approval__contract-link"
                      onClick={() => onNavigate(`/contracts/${contractNumber}`)}
                    >
                      {contractNumber}
                    </button>
                  ) : (
                    <span className="cp-approval__contract-id">
                      {contractNumber ?? "—"}
                    </span>
                  )}
                </div>
                <div
                  className={
                    "cp-approval__agent" +
                    (agentName === "Unassigned"
                      ? " cp-approval__agent--unassigned"
                      : "")
                  }
                >
                  {agentName}
                </div>
                <div className="cp-approval__net cp-cwidget__cell--right">
                  {formatCurrency(row.netPrice)}
                </div>
                <div className="cp-cwidget__cell--right">
                  <span
                    className={
                      "cp-approval__badge " +
                      (STATUS_BADGE_CLASS[status] ??
                        "cp-approval__badge--pending")
                    }
                  >
                    {status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </BaseWidget>
  );
}
